import React, { useEffect, useMemo, useRef, useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonTable from '../../../Components/CommonTable';
import CommonButton from '../../../Components/CommonButton';
import StatusBadge from '../../../Components/StatusBadge';
import { Body, SubHeading } from '../../../Constants/Fonts';
import { formatNumber } from './_shared';
import { mentionDate } from './topicKeywords';

const SENTIMENTS = ['positive', 'neutral', 'negative'];
const SENTIMENT_LABEL = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
const BUSIEST_DAYS = 5;

const CHART_H = 300;
const MARGIN = { top: 28, right: 16, bottom: 28, left: 40 };

const dayKey = d => d.toISOString().slice(0, 10);
const fmtDay = k => new Date(`${k}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDayLong = k => new Date(`${k}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const PLATFORM_LABEL = {
    x: 'X', twitter: 'X', linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok',
    reddit: 'Reddit', bluesky: 'Bluesky', facebook: 'Facebook', instagram: 'Instagram',
};
const platformLabel = p => PLATFORM_LABEL[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Other');

function buildDays(topic) {
    const axis = (topic.sparkline || []).map(s => s.date);
    const prior = topic.prior_sparkline || [];
    const byDay = Object.fromEntries(axis.map((date, i) => [date, {
        date, positive: 0, neutral: 0, negative: 0, total: 0, engagement: 0,
        platforms: {}, prior: prior[i]?.mentions ?? 0,
    }]));
    (topic.matches || []).forEach(m => {
        const when = mentionDate(m);
        const row = when && byDay[dayKey(when)];
        if (!row) return;
        const s = String(m.sentiment || 'neutral').toLowerCase();
        row[SENTIMENTS.includes(s) ? s : 'neutral'] += 1;
        row.total += 1;
        row.engagement += Number(m.engagement) || 0;
        const p = String(m.platform || 'other').toLowerCase();
        row.platforms[p] = (row.platforms[p] || 0) + 1;
    });
    return axis.map(d => {
        const row = byDay[d];
        const top = Object.entries(row.platforms).sort((a, b) => b[1] - a[1])[0];
        return { ...row, topPlatform: top ? top[0] : null };
    });
}

function niceTicks(max) {
    if (max <= 0) return [0, 1];
    const rough = max / 4;
    const mag = 10 ** Math.floor(Math.log10(rough));
    const step = Math.max(1, [1, 2, 5, 10].map(n => n * mag).find(s => s >= rough));
    const top = Math.ceil(max / step) * step;
    const ticks = [];
    for (let v = 0; v <= top; v += step) ticks.push(v);
    return ticks;
}

function useWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, width];
}

function TrendLineChart({ days, title }) {
    const [ref, width] = useWidth();
    const [hover, setHover] = useState(null);
    const n = days.length;
    const innerW = Math.max(0, width - MARGIN.left - MARGIN.right);
    const innerH = CHART_H - MARGIN.top - MARGIN.bottom;
    const ticks = niceTicks(Math.max(1, ...days.map(d => Math.max(d.total, d.prior))));
    const top = ticks[ticks.length - 1] || 1;
    const step = n > 1 ? innerW / (n - 1) : 0;
    const x = i => MARGIN.left + (n > 1 ? i * step : innerW / 2);
    const y = v => MARGIN.top + innerH - (v / top) * innerH;
    const baseline = MARGIN.top + innerH;
    const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(innerW / 70))));

    const line = key => days.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${y(d[key])}`).join(' ');
    const area = n ? `${line('total')} L${x(n - 1)},${baseline} L${x(0)},${baseline} Z` : '';
    const peakIdx = days.reduce((best, d, i) => (d.total > (days[best]?.total ?? -1) ? i : best), 0);
    const peak = days[peakIdx];
    const hoverDay = hover != null ? days[hover] : null;

    return (
        <div className="bm_trend_chart" ref={ref}>
            {width > 0 && n > 0 && (
                <svg width={width} height={CHART_H} role="img"
                    aria-label={`Daily mentions for ${title}, with the previous period as a dashed line`}>
                    {ticks.map(t => (
                        <g key={t}>
                            <line className="bm_trend_grid" x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} />
                            <text className="bm_trend_axis_text" x={MARGIN.left - 8} y={y(t)} dy="0.32em" textAnchor="end">{formatNumber(t)}</text>
                        </g>
                    ))}
                    {days.map((d, i) => (i % labelEvery === 0 || i === n - 1) && (
                        <text key={d.date} className="bm_trend_axis_text" x={x(i)} y={CHART_H - 8}
                            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{fmtDay(d.date)}</text>
                    ))}

                    <path className="bm_trend_area" d={area} />
                    <path className="bm_trend_prior" d={line('prior')} />
                    <path className="bm_trend_line" d={line('total')} />

                    {peak && peak.total > 0 && (
                        <g>
                            <circle className="bm_trend_point" cx={x(peakIdx)} cy={y(peak.total)} r="4.5" />
                            <text className="bm_trend_peak_label" x={x(peakIdx)} y={y(peak.total) - 12}
                                textAnchor={peakIdx < n * 0.1 ? 'start' : peakIdx > n * 0.9 ? 'end' : 'middle'}>
                                Busiest: {peak.total} on {fmtDay(peak.date)}
                            </text>
                        </g>
                    )}

                    {hoverDay && (
                        <g>
                            <line className="bm_trend_crosshair" x1={x(hover)} x2={x(hover)} y1={MARGIN.top} y2={baseline} />
                            <circle className="bm_trend_point is-prior" cx={x(hover)} cy={y(hoverDay.prior)} r="3.5" />
                            <circle className="bm_trend_point" cx={x(hover)} cy={y(hoverDay.total)} r="4.5" />
                        </g>
                    )}

                    {days.map((d, i) => {
                        const half = n > 1 ? step / 2 : innerW / 2;
                        return (
                            <rect key={`hit-${d.date}`} className="bm_trend_hit"
                                x={x(i) - half} y={MARGIN.top} width={half * 2} height={innerH}
                                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
                        );
                    })}
                </svg>
            )}
            {hoverDay && width > 0 && (
                <div className={`bm_trend_tooltip ${x(hover) > width / 2 ? 'is-left' : ''}`} style={{ left: x(hover) }}>
                    <Body as="div" variant={2} weight="semibold" className="bm_trend_tip_title">{fmtDayLong(hoverDay.date)}</Body>
                    <Body as="div" variant={2} className="bm_trend_tip_row">
                        <span><span className="bm_trend_swatch is-current" aria-hidden="true" /> Mentions</span>
                        <strong>{hoverDay.total}</strong>
                    </Body>
                    {hoverDay.total > 0 && SENTIMENTS.map(s => (
                        <Body as="div" variant={2} key={s} className="bm_trend_tip_row is-sub">
                            <span>{SENTIMENT_LABEL[s]}</span><strong>{hoverDay[s]}</strong>
                        </Body>
                    ))}
                    <Body as="div" variant={2} className="bm_trend_tip_row">
                        <span><span className="bm_trend_swatch is-prior" aria-hidden="true" /> Previous period</span>
                        <strong>{hoverDay.prior}</strong>
                    </Body>
                    <Body as="div" variant={2} className="bm_trend_tip_row">
                        <span>Engagement</span><strong>{formatNumber(hoverDay.engagement)}</strong>
                    </Body>
                    {hoverDay.topPlatform && (
                        <Body as="div" variant={2} className="bm_trend_tip_row">
                            <span>Top platform</span><strong>{platformLabel(hoverDay.topPlatform)}</strong>
                        </Body>
                    )}
                </div>
            )}
        </div>
    );
}

function LegendItem({ kind, label }) {
    return (
        <span className="bm_trend_legend_item">
            <span className={`bm_trend_swatch is-${kind}`} aria-hidden="true" />
            <Body as="span" variant={2} className="bm_trend_legend_text">{label}</Body>
        </span>
    );
}

function Kpi({ label, value, sub }) {
    return (
        <div className="bm_trend_kpi">
            <Body as="div" variant={2} weight="semibold" className="bm_trend_kpi_label">{label}</Body>
            <SubHeading as="div" className="bm_trend_kpi_value">{value}</SubHeading>
            {sub && <Body as="div" variant={2} className="bm_trend_kpi_sub">{sub}</Body>}
        </div>
    );
}

export default function TopicTrendModal({ topic, days: windowDays, onClose }) {
    const days = useMemo(() => buildDays(topic), [topic]);
    const [showAllDays, setShowAllDays] = useState(false);

    const total = days.reduce((a, d) => a + d.total, 0);
    const priorTotal = days.reduce((a, d) => a + d.prior, 0);
    const activeDays = days.filter(d => d.total > 0).length;
    const busiest = days.reduce((best, d) => (d.total > (best?.total || 0) ? d : best), null);
    const delta = priorTotal ? Math.round(((total - priorTotal) / priorTotal) * 100) : null;

    const tableDays = showAllDays
        ? [...days].reverse()
        : days.filter(d => d.total > 0)
            .sort((a, b) => b.total - a.total || b.engagement - a.engagement)
            .slice(0, BUSIEST_DAYS);

    return (
        <CommonModal
            show={true}
            onHide={onClose}
            size="xl"
            title={`${topic.title}: daily mentions`}
            subtitle={`Last ${windowDays} days, compared with the ${windowDays} days before. Hover any day for its detail.`}
        >
            <div className="bm_trend">
                <div className="bm_trend_kpis">
                    <Kpi label="Mentions" value={formatNumber(total)}
                        sub={delta == null
                            ? (total > 0 ? 'New: none in the previous period' : null)
                            : `${delta >= 0 ? '▲' : '▼'} ${Math.abs(delta)}% vs ${formatNumber(priorTotal)} before`} />
                    <Kpi label="Busiest day" value={busiest ? formatNumber(busiest.total) : '-'}
                        sub={busiest ? fmtDayLong(busiest.date) : 'No mentions yet'} />
                    <Kpi label="Daily average" value={(total / Math.max(1, days.length)).toFixed(1)} sub="mentions per day" />
                    <Kpi label="Active days" value={`${activeDays} / ${days.length}`} sub="days with at least one mention" />
                </div>

                <div>
                    <div className="bm_trend_legend">
                        <LegendItem kind="current" label={`Last ${windowDays} days`} />
                        <LegendItem kind="prior" label="Previous period" />
                    </div>
                    <TrendLineChart days={days} title={topic.title} />
                </div>

                <div>
                    <div className="bm_trend_section_head">
                        <SubHeading as="h4" variant={3} className="bm_trend_title">
                            {showAllDays ? 'Every day' : 'Busiest days'}
                        </SubHeading>
                        <CommonButton variant="secondary"
                            text={showAllDays ? 'Show busiest days' : `Show all ${days.length} days`}
                            onClick={() => setShowAllDays(v => !v)} />
                    </div>
                    {tableDays.length === 0 ? (
                        <Body variant={2} className="bm_trend_empty">No mentions in this period.</Body>
                    ) : (
                        <CommonTable
                            noScroll
                            headers={['Date', 'Mentions', 'Previous period', 'Positive', 'Neutral', 'Negative', 'Engagement', 'Top platform']}
                            rows={tableDays.map(d => ({
                                data: [
                                    fmtDayLong(d.date),
                                    formatNumber(d.total),
                                    formatNumber(d.prior),
                                    formatNumber(d.positive),
                                    formatNumber(d.neutral),
                                    d.negative > 0
                                        ? <StatusBadge variant="danger" dot={false} transform="none">{formatNumber(d.negative)}</StatusBadge>
                                        : '0',
                                    formatNumber(d.engagement),
                                    d.topPlatform ? platformLabel(d.topPlatform) : '-',
                                ],
                            }))}
                        />
                    )}
                </div>
            </div>
        </CommonModal>
    );
}
